"use client";

import { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { ComparisonData } from "./types";
import ComparisonCard from "./ComparisonCard";
import CountryFlag from "@/components/ui/CountryFlag";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import Accordion from "@/components/ui/Accordion";
import { Calendar, BookOpen } from "lucide-react";
import { SITE_AUTHOR } from "@/lib/constants/eeat";
import EeatTrustBadge from "@/components/layout/EeatTrustBadge";
import { countryComparisonGuides } from "@/data/comparisons/country-comparison-guides";

interface CompareDirectoryClientProps {
  comparisonsList: ComparisonData[];
  initialCountry?: string;
}

export default function CompareDirectoryClient({ comparisonsList, initialCountry }: CompareDirectoryClientProps) {
  const [selectedCountry, setSelectedCountry] = useState<string>("all");

  // Country filters are derived from the live comparison data so a country
  // with zero surviving comparisons (e.g. CH after the PRD-08 deletions)
  // never renders an empty filter.
  const countries = useMemo(() => {
    const COUNTRY_NAMES: Record<string, string> = {
      us: "United States",
      in: "India",
      gb: "United Kingdom",
      au: "Australia",
      ca: "Canada",
      sg: "Singapore",
      nz: "New Zealand",
      de: "Germany",
      ch: "Switzerland",
      nl: "Netherlands",
    };
    return [
      { code: "all", name: "All Countries" },
      ...Object.keys(COUNTRY_NAMES)
        .filter((code) => comparisonsList.some((c) => c.country.toLowerCase() === code))
        .map((code) => ({ code, name: COUNTRY_NAMES[code] })),
    ];
  }, [comparisonsList]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const countryParam = params.get("country")?.toLowerCase();
    if (countryParam && countries.some((c) => c.code === countryParam)) {
      setSelectedCountry(countryParam);
    } else if (initialCountry && countries.some((c) => c.code === initialCountry)) {
      setSelectedCountry(initialCountry);
    } else if (typeof window !== "undefined") {
      const storedCountry = localStorage.getItem("selectedCountry");
      if (storedCountry && countries.some((c) => c.code === storedCountry)) {
        setSelectedCountry(storedCountry);
      }
    }
  }, [initialCountry, countries]);

  const filteredComparisons = comparisonsList.filter((c) => {
    return selectedCountry === "all" || c.country.toLowerCase() === selectedCountry.toLowerCase();
  });

  return (
    <div className="compare-directory-wrapper anim-fade-in">
      <div className="container py-8">
        <Breadcrumbs />

        <div className="page-header">
          <h1 className="page-title">Compare Financial Products</h1>
          <p className="page-description">
            Compare investments, retirement accounts, insurance types, tax regimes, and mortgages side-by-side.
            Use our interactive widgets to calculate real-time projections.
          </p>
        </div>

        <div className="country-filters-scroll">
          <div className="country-filters">
            {countries.map((c) => (
              <button
                key={c.code}
                className={`filter-btn ${selectedCountry === c.code ? "active-filter" : ""}`}
                onClick={() => setSelectedCountry(c.code)}
              >
                {c.code !== "all" && (
                  <CountryFlag code={c.code} name={c.name} className="country-flag-image-xs" />
                )}
                {c.code === "all" && <span className="filter-flag">🌐</span>}
                <span className="filter-name">{c.name}</span>
              </button>
            ))}
          </div>
        </div>

        <p className="result-count">
          {filteredComparisons.length} {filteredComparisons.length === 1 ? "comparison" : "comparisons"} found
          {selectedCountry !== "all" && <> &middot; <strong>{countries.find(c => c.code === selectedCountry)?.name}</strong></>}
        </p>

        {/* h2 keeps the outline intact: ComparisonCard titles are h3s, which
            sat directly under the page h1 (a level skip). */}
        <h2 className="sr-only">All comparisons</h2>
        <div className="comparison-grid">
          {filteredComparisons.map((comp) => (
            <ComparisonCard key={comp.slug} comparison={comp} />
          ))}
        </div>

        {filteredComparisons.length === 0 && (
          <div className="empty-state-card">
            <p className="empty-text">No comparisons found for the selected filters.</p>
          </div>
        )}

        {/* Dynamic Educational Guide Section */}
        {(() => {
          const guide = countryComparisonGuides[selectedCountry.toLowerCase()] || countryComparisonGuides["all"];
          return (
            <>
              {guide && (
                <>
                  <div className="section-divider"></div>
                  <section className="educational-guide-section">
                    <h2 className="section-heading section-heading--rule">
                      {guide.title}
                    </h2>
                    <p className="lead-text" style={{ marginBottom: "var(--space-6)" }}>
                      {guide.intro}
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
                      {guide.sections.map((sec, idx) => (
                        <div key={idx}>
                          <h3 className="subsection-heading">
                            {sec.heading}
                          </h3>
                          <p style={{ lineHeight: 1.7, color: "var(--text-secondary)", margin: 0 }}>
                            {sec.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>
                </>
              )}

              {/* E-E-A-T Trust Badge */}
              <div className="section-divider"></div>
              <EeatTrustBadge
                label="Research-Backed Tools"
                metaText="Each comparison shows its own review date. Check current regulator rules, policy terms and written quotes before using the figures."
                style={{ marginTop: 0, marginBottom: 0 }}
              />

              {/* Dynamic Country FAQs Section */}
              {guide && guide.faqs && guide.faqs.length > 0 && (
                <>
                  <div className="section-divider"></div>
                  <section className="faq-section">
                    <h2 className="section-heading section-heading--rule">
                      Frequently Asked Questions
                    </h2>
                    <Accordion
                      items={guide.faqs.map((faq) => ({
                        title: faq.question,
                        content: <span>{faq.answer}</span>
                      }))}
                    />
                  </section>
                </>
              )}
            </>
          );
        })()}

        {/* Related reading links */}
        <div className="section-divider"></div>
        <section className="related-reading-section">
          <div className="related-reading-card">
            <div className="related-reading-icon">
              <BookOpen size={24} />
            </div>
            <div className="related-reading-content">
              <h3 className="related-reading-title">Understand the basics before you compare</h3>
              <p className="related-reading-text">
                Our blog covers how each financial product works, what the key terms mean, and what changed in the
                2026 tax and regulatory updates. Read up on a topic before running the comparison.
              </p>
              <Link href="/blog" className="related-reading-link">
                Browse financial guides
              </Link>
            </div>
          </div>
        </section>

      <style jsx>{`
        .compare-directory-wrapper {
          padding-bottom: var(--space-12);
        }

        .page-header {
          margin-bottom: var(--space-3);
        }

        .page-title {
          font-family: var(--font-heading);
          font-size: var(--text-3xl);
          color: var(--text-primary);
          font-weight: var(--fw-extrabold);
          letter-spacing: var(--tracking-tight);
          line-height: var(--lh-tight);
          margin-bottom: var(--space-2);
        }
        .page-description {
          font-family: var(--font-body);
          font-size: var(--text-md);
          color: var(--text-secondary);
          line-height: var(--lh-relaxed);
          font-weight: var(--fw-normal);
          max-width: 100%;
          width: 100%;
        }

        .country-filters-scroll {
          overflow-x: auto;
          padding-bottom: var(--space-1);
          margin-bottom: var(--space-2);
        }
        .country-filters-scroll::-webkit-scrollbar {
          height: 3px;
        }
        .country-filters-scroll::-webkit-scrollbar-thumb {
          background-color: var(--border-color);
          border-radius: var(--radius-full);
        }
        .country-filters {
          display: flex;
          gap: var(--space-2);
          width: max-content;
        }
        .filter-btn {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-full);
          padding: 6px 14px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: background-color 0.2s, border-color 0.2s, color 0.2s, box-shadow 0.2s;
          font-family: var(--font-body);
          font-size: var(--text-xs);
          font-weight: var(--fw-semibold);
          white-space: nowrap;
        }
        .filter-btn:hover {
          background-color: var(--bg-elevated);
          border-color: var(--border-color-hover);
          color: var(--text-primary);
        }
        .active-filter {
          background-color: var(--info-light);
          border-color: var(--info);
          color: var(--info-fg);
        }
        .filter-flag {
          font-size: var(--text-base);
        }

        .result-count {
          font-size: var(--text-xs);
          color: var(--text-muted);
          margin-bottom: var(--space-3);
          font-weight: var(--fw-medium);
        }

        .comparison-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: var(--space-5);
        }

        .empty-state-card {
          background-color: var(--bg-surface);
          border: 1px dashed var(--border-color);
          border-radius: var(--radius-md);
          padding: var(--space-12);
          text-align: center;
        }
        .empty-text {
          font-family: var(--font-body);
          color: var(--text-muted);
          font-weight: var(--fw-semibold);
        }

        .section-title {
          font-family: var(--font-heading);
          font-size: var(--text-2xl);
          color: var(--text-primary);
          font-weight: var(--fw-extrabold);
          margin-bottom: var(--space-4);
          letter-spacing: -0.01em;
          line-height: 1.3;
        }

        @media (max-width: 767px) {
          .section-title {
            font-size: var(--text-xl);
          }
        }

        .methodology-text {
          font-family: var(--font-body);
          font-size: var(--text-sm);
          color: var(--text-secondary);
          line-height: 1.7;
          margin-bottom: var(--space-3);
          font-weight: var(--fw-medium);
        }

        .methodology-text:last-child {
          margin-bottom: 0;
        }

        .faq-section {
          margin-bottom: 0;
        }

        .related-reading-card {
          display: flex;
          gap: var(--space-4);
          background: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: var(--space-5);
          align-items: flex-start;
        }

        .related-reading-icon {
          color: var(--primary);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .related-reading-content {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }

        .related-reading-title {
          font-family: var(--font-heading);
          font-size: var(--text-base);
          font-weight: var(--fw-bold);
          color: var(--text-primary);
          margin: 0;
        }

        .related-reading-text {
          font-size: var(--text-sm);
          color: var(--text-secondary);
          line-height: 1.5;
          font-weight: var(--fw-medium);
          margin: 0;
        }

        .related-reading-link {
          font-size: var(--text-sm);
          font-weight: var(--fw-bold);
          color: var(--primary);
          text-decoration: underline;
          width: fit-content;
        }

        .related-reading-link:hover {
          color: var(--primary-hover);
        }
      `}</style>
    </div>
    </div>
  );
}
